import React, {useCallback, useEffect, useMemo, useState} from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import {useTranslation, withTranslation} from "react-i18next";
import {Virtuoso} from "react-virtuoso";
import {compose} from "redux";
import {getLabelField} from "./constants";

const linkIconStyle = {
  margin: "0 !important",
  color: theme => theme.palette.text.primary
};

const getAttributeValue = attribute => {
  let attributeValue = "";
  if (attribute) {
    if (typeof attribute === "string") {
      attributeValue = attribute;
    } else {
      try {
        attributeValue = JSON.stringify(attribute);
      } catch {
        attributeValue = "";
      }
    }
  }

  return attributeValue;
};

const renderDetailsEntries = (details, wmsServiceUrlField) => (
  <Box
    component="table"
    className="viewer-wms-map__identify-results__item__details__table"
    sx={{
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "auto"
    }}
  >
    <Box component="tbody">
      {Object.keys(details ?? {})
        .filter(key => (wmsServiceUrlField ? key !== wmsServiceUrlField : true))
        .map((key, idx) => (
          <Box component="tr" key={idx} className="viewer-wms-map__identify-results__item__details__element">
            <Box
              component="td"
              className="viewer-wms-map__identify-results__item__details__element__key"
              sx={{
                overflowWrap: "break-word",
                verticalAlign: "top",
                paddingBottom: "4px",
                paddingRight: "8px",
                fontWeight: "bold"
              }}
            >
              {key}:
            </Box>
            <Box
              component="td"
              className="viewer-wms-map__identify-results__item__details__element__value"
              sx={{
                overflowWrap: "break-word",
                verticalAlign: "top",
                paddingBottom: "4px"
              }}
            >
              {getAttributeValue(details[key])}
            </Box>
          </Box>
        ))}
    </Box>
  </Box>
);

const IdentifyResultItem = ({
  feature,
  expanded,
  onChange,
  labelField,
  labelFieldList,
  wmsServiceUrlField,
  layerData,
  language,
  t
}) => (
  <>
    {layerData && (
      <Box
        id={`viewer-wms-map__identify-results__layer-title--${layerData.layer}`}
        className="viewer-wms-map__identify-results__layer-title"
        sx={{
          padding: "8px 12px 0px"
        }}
      >
        {layerData.layer + " (" + layerData.count + ")"}
      </Box>
    )}
    <Accordion
      id={`viewer-wms-map__identify-results__item--${feature.uniqueId}`}
      className="viewer-wms-map__identify-results__item"
      expanded={expanded}
      onChange={onChange}
    >
      <AccordionSummary
        className="viewer-wms-map__identify-results__item__summary"
        sx={{
          padding: "4px 16px"
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography
          sx={{
            fontWeight: expanded ? "bold" : "normal"
          }}
        >
          {getLabelField(feature, labelField, labelFieldList)}
        </Typography>
      </AccordionSummary>
      <Divider
        className="viewer-wms-map__identify-results__item__details-divider"
        sx={{
          margin: "0 8px"
        }}
      />
      <AccordionDetails className="viewer-wms-map__identify-results__item__details" sx={{fontSize: "14px"}}>
        {renderDetailsEntries(feature?.attributes, wmsServiceUrlField)}
      </AccordionDetails>
      {feature.attributes[wmsServiceUrlField] && (
        <>
          <Divider
            className="viewer-wms-map__identify-results__item__actions-divider"
            sx={{
              margin: "0 8px"
            }}
          />
          <AccordionActions className="viewer-wms-map__identify-results__item__actions">
            <Tooltip title={t("modules.viewerGeojsonMap.identifyDialog.links.externalWmsService")}>
              <IconButton
                href={`/${language}/wms?serviceUrl=${feature.attributes[wmsServiceUrlField]}`}
                target="_blank"
                sx={linkIconStyle}
              >
                <LinkIcon
                  className="viewer-wms-map__identify-results__item__actions__external-wms-icon"
                  fontSize="small"
                />
              </IconButton>
            </Tooltip>
          </AccordionActions>
        </>
      )}
    </Accordion>
  </>
);

const IdentifyResults = ({
  visibleLayers,
  identifyResults,
  labelField,
  labelFieldList,
  wmsServiceUrlFieldList,
  language
}) => {
  const {t} = useTranslation();
  const [expandedFeatureId, setExpandedFeatureId] = useState(null);

  const orderedFeatures = useMemo(() => {
    return (visibleLayers || [])
      .filter(layer => layer in (identifyResults || {}))
      .flatMap(layer => identifyResults[layer] || []);
  }, [visibleLayers, identifyResults]);

  const uniqueIdToLayer = useMemo(() => {
    const map = {};
    for (const layer of visibleLayers || []) {
      const features = identifyResults?.[layer] || [];
      const uniqueId = features[0]?.uniqueId;

      if (uniqueId !== null) {
        map[uniqueId] = {
          layer: layer,
          count: features.length
        };
      }
    }
    return map;
  }, [visibleLayers, identifyResults]);

  const handleSelectFeature = useCallback(
    id => (_, isExpanded) => {
      if ((orderedFeatures || []).length > 0) {
        setExpandedFeatureId(isExpanded ? id : null);
      } else {
        setExpandedFeatureId(null);
      }
    },
    [orderedFeatures]
  );

  useEffect(() => {
    setExpandedFeatureId(null);
    if ((orderedFeatures || []).length > 0) {
      const uniqueId = orderedFeatures[0].uniqueId;
      setExpandedFeatureId(uniqueId);
    }
  }, [orderedFeatures]);

  return (
    <Box
      className="viewer-wms-map__identify-results"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "& .viewer-wms-map__identify-results__item": {
          margin: "8px 0 !important"
        },
        "& .viewer-wms-map__identify-results__item:first-of-type": {
          marginTop: "0 !important"
        }
      }}
    >
      {(orderedFeatures || []).length > 0 && (
        <Virtuoso
          data={orderedFeatures}
          itemContent={(idx, feature) => {
            const wmsServiceUrlField =
              (wmsServiceUrlFieldList || []).length === 0
                ? null
                : wmsServiceUrlFieldList.find(key => feature?.attributes?.[key]) || null;
            const layerData = uniqueIdToLayer[feature.uniqueId] ?? null;
            return (
              <IdentifyResultItem
                key={idx}
                feature={feature}
                expanded={expandedFeatureId === feature.uniqueId}
                onChange={handleSelectFeature(feature.uniqueId)}
                labelField={labelField}
                labelFieldList={labelFieldList}
                wmsServiceUrlField={wmsServiceUrlField}
                layerData={layerData}
                language={language}
                t={t}
              />
            );
          }}
        />
      )}
    </Box>
  );
};

export default compose(withTranslation())(IdentifyResults);
